
import React, {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';


import {ChunkedDownloadTool} from 'src/components/chuncked-download-tool';
import {MAX_SELECTED_ENTITIES} from 'src/constants';
import {useLicenseTypes} from 'src/hooks/dictionaries';
import {getAccountsActiveLicenses} from 'src/openapi/admin/apis/AccountActiveLicenseApi';
import {getAccountsManagers} from 'src/openapi/admin/apis/AccountManagerRelationsApi';
import {downloadAccountInfo} from 'src/openapi/admin/apis/AccountSearchDownloadRequestApi';
import {getAccountsUsersCountByState} from 'src/openapi/admin/apis/AccountsUsersCountsApi';
import {getAccountsCrmModules} from 'src/openapi/admin/apis/CrmModuleSettingsControllerApi';
import {getAccountName} from 'src/openapi/admin/apis/GetAccountNameApi';
import {getAccountsByIds} from 'src/openapi/admin/apis/GetAccountsApi';
import {LicenseType} from 'src/openapi/admin/models/LicenseType';
import {UserState} from 'src/openapi/admin/models/UserState';
import {useAppDispatch} from 'src/store/hooks';
import {getSuccessPayload} from 'src/utils/getSuccessPayload';
import {getSeatsRangeTitle} from 'src/utils/license-util';

import {FormattedDate} from '@pb/kit-component-formatters';

import {COLUMNS, HEADERS} from '../../constants';
import {useGetColumnValue} from '../../hooks/useGetColumnValue';
import {selectSelectedColumns} from '../../slices/account-search-result-columns.slice';
import {accountSearchResultResultSelector} from '../../slices/account-search-result.slice';

import {ACCOUNT_DOWNLOAD_FILE_NAME, ACCOUNT_DOWNLOAD_TASK_NAME} from './constants';

import type {GetData} from 'src/components/chuncked-download-tool/typedef';
import type {Error} from 'src/openapi/admin/models/Error';

type Props = {
    accountIds: number[],
    children: (handler: () => void) => React.ReactNode,
    getSortedEntityIds: () => Promise<{
        type: string,
        payload: {
            data: number[] | string[],
            success: true,
            error: false,
        } | {
            data: Error,
            success: false,
            error: true,
        },
    }>,
};

export const AccountSearchDownloadTool = ({accountIds, children, getSortedEntityIds}: Props) => {
    const apiDispatch = useAppDispatch();
    const selectedColumns = useSelector(selectSelectedColumns);
    const result = useSelector(accountSearchResultResultSelector);

    const {getLicenseTypeByCode} = useLicenseTypes();

    const {getColumnValue} = useGetColumnValue();

    const fileHeaders = useMemo(() => {
        return selectedColumns.reduce((acc, curr) => ({...acc, [curr]: HEADERS[curr]}), {});
    }, [selectedColumns]);

    const getData: GetData<Record<string, string>> = useCallback(async (selectedIds: number[]) => {
        let data: Record<string, string>[] = [];
        let fetchingFailed = false;

        let ids: number[] = [];

        if (result && result.totalCount < MAX_SELECTED_ENTITIES) {
            const sortedEntityIdsResponse = await getSortedEntityIds();
            if (sortedEntityIdsResponse.payload.success) {
                const sortedEntityIds = sortedEntityIdsResponse.payload.data as number[];
                ids = sortedEntityIds.filter(id => selectedIds.includes(id));
            } else {
                fetchingFailed = true;
            }
        } else {
            ids = selectedIds;
        }

        const baseResults = await apiDispatch(getAccountsByIds({requestBody: ids}));
        if (baseResults.payload.success) {
            const baseResultsResponse = baseResults.payload.data;

            const accounts = ids.map(id => baseResultsResponse[id]);
            const nameToIdsMap = accounts.reduce<Record<string, number>>((acc, curr) => {
                acc[curr.accountName] = curr.accountId;
                return acc;
            }, {});

            data = accounts.map(account => selectedColumns.reduce<Record<string, string>>((acc, curr) => {
                const value = getColumnValue(curr, account);
                acc[curr] = value === undefined || value === null ? '' : String(value);
                return acc;
            }, {}));

            if (selectedColumns.includes(COLUMNS.PARENT_ACCOUNT_NAME)) {
                const parentAccountIds = accounts.map(account => account.parentAccountId).filter(Boolean) as number[];
                const accountNamesResponse = await apiDispatch(getAccountName({
                    requestBody: [...new Set(parentAccountIds)],
                }));

                const accountNames = getSuccessPayload(accountNamesResponse.payload);
                if (accountNames) {
                    data = data.map(accountRow => {
                        const parentAccountName = accountRow[COLUMNS.PARENT_ACCOUNT_NAME];
                        return ({
                            ...accountRow,
                            [COLUMNS.PARENT_ACCOUNT_NAME]: parentAccountName
                                ? accountNames[parentAccountName] ?? ''
                                : '',
                        });
                    });
                } else {
                    fetchingFailed = true;
                }
            }

            const applyCounts = (column: string, counts: Record<string, number> | null | undefined) => {
                if (!counts) {
                    fetchingFailed = true;
                    return;
                }

                data = data.map(accountRow => {
                    const accountName = accountRow[COLUMNS.ACCOUNT_NAME];
                    const accountId = accountName ? nameToIdsMap[accountName] : undefined;
                    const value = accountId !== undefined ? counts[String(accountId)] : undefined;

                    return ({
                        ...accountRow,
                        [column]: value !== undefined ? String(value) : '',
                    });
                });
            };

            if (selectedColumns.includes(COLUMNS.LICENSED_ENABLED_USERS)) {
                const countsResponse = await apiDispatch(getAccountsUsersCountByState({
                    getAccountsUsersCountsByStateRequest: {
                        accountIds: [...new Set(accountIds)],
                        userState: UserState.LICENSED,
                    }}),
                );

                applyCounts(COLUMNS.LICENSED_ENABLED_USERS, getSuccessPayload(countsResponse.payload));
            }

            if (selectedColumns.includes(COLUMNS.MOBILE_ENABLED_USERS)) {
                const countsResponse = await apiDispatch(getAccountsUsersCountByState({
                    getAccountsUsersCountsByStateRequest: {
                        accountIds: [...new Set(accountIds)],
                        userState: UserState.MOBILE,
                    }}),
                );

                applyCounts(COLUMNS.MOBILE_ENABLED_USERS, getSuccessPayload(countsResponse.payload));
            }

            if (selectedColumns.includes(COLUMNS.CRM_ENABLED_USERS)) {
                const countsResponse = await apiDispatch(getAccountsUsersCountByState({
                    getAccountsUsersCountsByStateRequest: {
                        accountIds: [...new Set(accountIds)],
                        userState: UserState.CRM_ONLY,
                    }}),
                );

                applyCounts(COLUMNS.CRM_ENABLED_USERS, getSuccessPayload(countsResponse.payload));
            }

            if (selectedColumns.includes(COLUMNS.DISABLED_NEWSLETTER_USERS)) {
                const countsResponse = await apiDispatch(getAccountsUsersCountByState({
                    getAccountsUsersCountsByStateRequest: {
                        accountIds: [...new Set(accountIds)],
                        userState: UserState.DISABLED,
                    }}),
                );

                applyCounts(COLUMNS.DISABLED_NEWSLETTER_USERS, getSuccessPayload(countsResponse.payload));
            }

            if (selectedColumns.includes(COLUMNS.NO_ACCESS_USERS)) {
                const countsResponse = await apiDispatch(getAccountsUsersCountByState({
                    getAccountsUsersCountsByStateRequest: {
                        accountIds: [...new Set(accountIds)],
                        userState: UserState.NO_ACCESS,
                    }}),
                );

                applyCounts(COLUMNS.NO_ACCESS_USERS, getSuccessPayload(countsResponse.payload));
            }

            if (selectedColumns.includes(COLUMNS.CONFERENCE_USERS)) {
                const countsResponse = await apiDispatch(getAccountsUsersCountByState({
                    getAccountsUsersCountsByStateRequest: {
                        accountIds: [...new Set(accountIds)],
                        userState: UserState.CONFERENCE,
                    }}),
                );

                applyCounts(COLUMNS.CONFERENCE_USERS, getSuccessPayload(countsResponse.payload));
            }

            if (selectedColumns.includes(COLUMNS.CREDIT_ALERTS_ENABLED_USERS)) {
                const countsResponse = await apiDispatch(getAccountsUsersCountByState({
                    getAccountsUsersCountsByStateRequest: {
                        accountIds: [...new Set(accountIds)],
                        userState: UserState.CREDIT_ALERTS,
                    }}),
                );

                applyCounts(COLUMNS.CREDIT_ALERTS_ENABLED_USERS, getSuccessPayload(countsResponse.payload));
            }

            if ([
                COLUMNS.SALESFORCE_INTEGRATION,
                COLUMNS.MS_DYNAMICS_INTEGRATION,
                COLUMNS.DEAL_CLOUD_INTEGRATION,
                COLUMNS.HUBSPOT_INTEGRATION,
            ].some(column => selectedColumns.includes(column))) {
                const crmIntegrationResponse = await apiDispatch(getAccountsCrmModules({
                    requestBody: accountIds,
                }));

                const crmIntegration = getSuccessPayload(crmIntegrationResponse.payload);
                if (crmIntegration) {

                    if (selectedColumns.includes(COLUMNS.SALESFORCE_INTEGRATION)) {
                        data = data.map(accountRow => {
                            const accountName = accountRow[COLUMNS.ACCOUNT_NAME];
                            const accountId = accountName ? nameToIdsMap[accountName] : undefined;
                            const sfdcEnabled = accountId !== undefined
                                ? crmIntegration[String(accountId)]?.sfdcEnabled
                                : undefined;

                            return ({
                                ...accountRow,
                                [COLUMNS.SALESFORCE_INTEGRATION]: sfdcEnabled ? 'Yes' : 'No',
                            });
                        });
                    }

                    if (selectedColumns.includes(COLUMNS.MS_DYNAMICS_INTEGRATION)) {
                        data = data.map(accountRow => {
                            const accountName = accountRow[COLUMNS.ACCOUNT_NAME];
                            const accountId = accountName ? nameToIdsMap[accountName] : undefined;
                            const msdEnabled = accountId !== undefined
                                ? crmIntegration[String(accountId)]?.msdEnabled
                                : undefined;

                            return ({
                                ...accountRow,
                                [COLUMNS.MS_DYNAMICS_INTEGRATION]: msdEnabled ? 'Yes' : 'No',
                            });
                        });
                    }

                    if (selectedColumns.includes(COLUMNS.DEAL_CLOUD_INTEGRATION)) {
                        data = data.map(accountRow => {
                            const accountName = accountRow[COLUMNS.ACCOUNT_NAME];
                            const accountId = accountName ? nameToIdsMap[accountName] : undefined;
                            const dealCloudEnabled = accountId !== undefined
                                ? crmIntegration[String(accountId)]?.dealCloudEnabled
                                : undefined;

                            return ({
                                ...accountRow,
                                [COLUMNS.DEAL_CLOUD_INTEGRATION]: dealCloudEnabled ? 'Yes' : 'No',
                            });
                        });
                    }

                    if (selectedColumns.includes(COLUMNS.HUBSPOT_INTEGRATION)) {
                        data = data.map(accountRow => {
                            const accountName = accountRow[COLUMNS.ACCOUNT_NAME];
                            const accountId = accountName ? nameToIdsMap[accountName] : undefined;
                            const hubspotEnabled = accountId !== undefined
                                ? crmIntegration[String(accountId)]?.hubspotEnabled
                                : undefined;

                            return ({
                                ...accountRow,
                                [COLUMNS.HUBSPOT_INTEGRATION]: hubspotEnabled ? 'Yes' : 'No',
                            });
                        });
                    }
                } else {
                    fetchingFailed = true;
                }
            }

            if (selectedColumns.includes(COLUMNS.PRIMARY_ACCOUNT_MANAGER)) {
                const managersResponse = await apiDispatch(getAccountsManagers({
                    requestBody: [...new Set(accountIds)],
                }));

                const managers = getSuccessPayload(managersResponse.payload);
                if (managers) {
                    data = data.map(accountRow => {
                        const accountName = accountRow[COLUMNS.ACCOUNT_NAME];
                        const accountId = accountName ? nameToIdsMap[accountName] : undefined;
                        const manager = accountId !== undefined ? managers[String(accountId)] : undefined;

                        const label = manager ? [manager.firstName, manager.lastName]
                            .filter(Boolean).join(' ') || manager.email : '';
                        return ({
                            ...accountRow,
                            [COLUMNS.PRIMARY_ACCOUNT_MANAGER]: label,
                        });
                    });
                } else {
                    fetchingFailed = true;
                }
            }

            if ([
                COLUMNS.LICENSE_TYPE,
                COLUMNS.LICENSE_START_DATE,
                COLUMNS.LICENSE_END_DATE,
                COLUMNS.LICENSE_SEATS,
            ].some(column => selectedColumns.includes(column))) {
                const activeLicensesResponses = await apiDispatch(getAccountsActiveLicenses({
                    requestBody: accountIds,
                }));

                if (activeLicensesResponses.payload.success) {
                    const activeLicenses = activeLicensesResponses.payload.data;

                    if (selectedColumns.includes(COLUMNS.LICENSE_TYPE)) {
                        data = data.map(account => {
                            const licenseType = activeLicenses[nameToIdsMap[account[COLUMNS.ACCOUNT_NAME]]].licenseType;

                            return ({
                                ...account,
                                [COLUMNS.LICENSE_TYPE]: getLicenseTypeByCode(licenseType) || '',
                            });
                        });
                    }

                    if (selectedColumns.includes(COLUMNS.LICENSE_START_DATE)) {
                        data = data.map(account => {
                            const value = activeLicenses[nameToIdsMap[account[COLUMNS.ACCOUNT_NAME]]].licenseStarts;

                            return ({
                                ...account,
                                [COLUMNS.LICENSE_START_DATE]: FormattedDate.format({
                                    value,
                                    format: 'YYYY-MM-DD',
                                }) || '',
                            });
                        });
                    }

                    if (selectedColumns.includes(COLUMNS.LICENSE_END_DATE)) {
                        data = data.map(account => {
                            const value = activeLicenses[nameToIdsMap[account[COLUMNS.ACCOUNT_NAME]]].licenseEnds;

                            return ({
                                ...account,
                                [COLUMNS.LICENSE_END_DATE]: FormattedDate.format({
                                    value,
                                    format: 'YYYY-MM-DD',
                                }) || '',
                            });
                        });
                    }

                    if (selectedColumns.includes(COLUMNS.LICENSE_SEATS)) {
                        data = data.map(account => {
                            const accountId = nameToIdsMap[account[COLUMNS.ACCOUNT_NAME]];
                            const license = activeLicenses[accountId];
                            const value = (() => {
                                if (!license?.seatsCount) {
                                    return '';
                                }
                                return license.licenseType === LicenseType.US
                                    ? getSeatsRangeTitle(license.seatsCount).title
                                    : String(license.seatsCount);
                            })();
                            return {
                                ...account,
                                [COLUMNS.LICENSE_SEATS]: value,
                            };
                        });
                    }

                } else {
                    fetchingFailed = true;
                }
            }

            if ([
                COLUMNS.SSO_ENABLED,
                COLUMNS.SSO_MANDATORY,
            ].some(column => selectedColumns.includes(column))) {
                const ssoResponse = await apiDispatch(
                    downloadAccountInfo({entityDownloadInfoRequest: {ids: accountIds}}),
                );

                if (ssoResponse.payload.success) {
                    const ssoMap = new Map(
                        ssoResponse.payload.data.map(item => [item.id, item]),
                    );

                    data = data.map(accountRow => {
                        const accountName = accountRow[COLUMNS.ACCOUNT_NAME];
                        const accountId = accountName
                            ? nameToIdsMap[accountName]
                            : undefined;
                        const sso = accountId !== undefined
                            ? ssoMap.get(accountId)
                            : undefined;

                        return {
                            ...accountRow,
                            ...(selectedColumns.includes(COLUMNS.SSO_ENABLED) && {
                                [COLUMNS.SSO_ENABLED]: sso?.ssoEnabled ? 'Yes' : 'No',
                            }),
                            ...(selectedColumns.includes(COLUMNS.SSO_MANDATORY) && {
                                [COLUMNS.SSO_MANDATORY]: sso?.ssoMandatory
                                    ? 'Yes'
                                    : 'No',
                            }),
                        };
                    });
                } else {
                    fetchingFailed = true;
                }
            }
        } else {
            fetchingFailed = true;
        }

        if (!fetchingFailed) {
            return Promise.resolve({type: '', payload: {success: true, error: false, data}});
        } else {
            return Promise.resolve({type: '', payload: {
                success: false,
                error: true,
                data: {message: 'Failed to fetch data for the report'},
            }});
        }
    }, [
        apiDispatch,
        getLicenseTypeByCode,
        result,
        selectedColumns,
        getColumnValue,
        getSortedEntityIds,
        accountIds,
    ]);

    return (
        <ChunkedDownloadTool
            ids={accountIds}
            taskName={ACCOUNT_DOWNLOAD_TASK_NAME}
            fileName={ACCOUNT_DOWNLOAD_FILE_NAME}
            fileHeaders={fileHeaders}
            getData={getData}
            chunkSize={250}>
            {handler => children(handler)}
        </ChunkedDownloadTool>
    );
};
